import { type JSX, useEffect, useRef } from "react";

import { MenuOpen, PushPin, PushPinOutlined } from "@mui/icons-material";
import { Box, Collapse, IconButton, SvgIcon } from "@mui/material";
import {
  ConfigurationTree,
  ConfigurationTreeProps,
} from "@plansoft-configuration-renderer/components";
import { Gateway } from "@plansoft-configuration-renderer/gateway";

import { useConfigurationTreeContext } from "@/context/ConfigurationTreeContext";

import { CustomConfigurationTreeComponents } from "./CustomConfigurationTreeComponents";

/**
 * Shows a navigation tree of the configuration.
 *
 * Can be placed directly in the layout or directly in `<ConfigurationRenderer />` to control navigation.
 * @param props
 * @returns
 */
export function CustomConfigurationTree(
  props: ConfigurationTreeProps,
): JSX.Element {
  const { pinned, setPinned, expanded, setExpanded } =
    useConfigurationTreeContext();
  const treeContentRef = useRef<HTMLDivElement>(null);
  const treeButtonRef = useRef<HTMLButtonElement>(null);
  const treePinRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      const isClickOnTreeContent = treeContentRef.current?.contains(target);
      const isClickOnButton = treeButtonRef.current?.contains(target);
      const isClickOnPin = treePinRef.current?.contains(target);

      if (
        !isClickOnTreeContent &&
        !isClickOnButton &&
        !isClickOnPin &&
        expanded &&
        !pinned
      ) {
        setExpanded(!expanded);
      }
    };

    if (expanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded, pinned, setExpanded]);

  return (
    <Box>
      <Gateway into="custom-configuration-tree-pin">
        <Box sx={{ width: 32 }}>
          {expanded && (
            <IconButton ref={treePinRef} onClick={() => setPinned(!pinned)}>
              <SvgIcon
                component={pinned ? PushPin : PushPinOutlined}
                inheritViewBox
              />
            </IconButton>
          )}
        </Box>
      </Gateway>
      <Gateway into="custom-configuration-tree-button">
        <IconButton
          ref={treeButtonRef}
          onClick={() => setExpanded(!expanded)}
          sx={{
            transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.25s",
          }}
        >
          <MenuOpen />
        </IconButton>
      </Gateway>
      <Gateway into="custom-configuration-tree-content">
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 105,
            "@media (min-width:1400px)": {
              top: 65,
            },
          }}
        >
          <Collapse in={expanded} orientation="horizontal">
            <Box
              ref={treeContentRef}
              className="custom-configuration-tree-content"
              sx={{
                width: "232px",
                backgroundColor: "background.light",
                height: "calc(var(--app-root-height) - 122px)",
                "@media (min-width:1400px)": {
                  height: "calc(var(--app-root-height) - 81px)",
                },
              }}
            >
              <ConfigurationTree
                {...props}
                components={CustomConfigurationTreeComponents}
              />
            </Box>
          </Collapse>
        </Box>
      </Gateway>
    </Box>
  );
}
